import { useState, useEffect } from "react";
import { X, Send } from "lucide-react";
import { toast } from "sonner";

export default function HolographicContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+971",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Delay showing form content for entrance animation
      setTimeout(() => setShowForm(true), 100);
      document.body.style.overflow = "hidden";
    } else {
      setShowForm(false);
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setShowForm(false);
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSending(true);
    // Simulate API call
    setTimeout(() => {
      toast.success("Message transmitted successfully!");
      setFormData({ name: "", email: "", countryCode: "+971", phone: "", message: "" });
      setSending(false);
      handleClose();
    }, 1500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Transparent overlay */}
      <div
        className={`fixed inset-0 z-[9999] transition-all duration-500 flex items-center justify-center ${
          showForm ? "opacity-100" : "opacity-0"
        }`}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", backdropFilter: "blur(8px)" }}
        onClick={handleClose}
      >
        {/* Subtle ambient glow around form area */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Center glow effect */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(155, 0, 255, 0.15), transparent 60%)",
              filter: "blur(80px)",
            }}
          />
        </div>

        {/* Holographic Form Container */}
        <div
          className={`relative w-full max-w-lg px-4 max-h-[92vh] overflow-y-auto overscroll-contain transition-all duration-700 ${
            showForm
              ? "opacity-100 scale-100"
              : "opacity-0 scale-0"
          }`}
          style={{
            transform: showForm ? "scale(1) rotateX(0deg)" : "scale(0.1) rotateX(90deg)",
            transformStyle: "preserve-3d",
            perspective: "1000px",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(155, 0, 255, 0.5) transparent",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="relative p-5 md:p-7"
            style={{
              background: "linear-gradient(135deg, rgba(61, 0, 102, 0.6) 0%, rgba(13, 0, 16, 0.85) 50%, rgba(61, 0, 102, 0.4) 100%)",
              border: "1px solid rgba(155, 0, 255, 0.4)",
              borderRadius: "8px",
              backdropFilter: "blur(20px)",
              boxShadow: `
                0 0 0 1px rgba(155, 0, 255, 0.2),
                0 0 60px rgba(155, 0, 255, 0.4),
                0 0 120px rgba(155, 0, 255, 0.2),
                inset 0 0 80px rgba(61, 0, 102, 0.3),
                inset 0 1px 0 rgba(155, 0, 255, 0.3)
              `,
              animation: showForm ? "holoFlicker 6s ease-in-out infinite" : "none",
            }}
          >
            {/* Corner Brackets */}
            <div
              className="absolute top-0 left-0 w-6 h-6"
              style={{
                borderTop: "2px solid #9b00ff",
                borderLeft: "2px solid #9b00ff",
                boxShadow: "-3px -3px 15px rgba(155, 0, 255, 0.6), 0 0 10px #9b00ff",
              }}
            />
            <div
              className="absolute top-0 right-0 w-6 h-6"
              style={{
                borderTop: "2px solid #9b00ff",
                borderRight: "2px solid #9b00ff",
                boxShadow: "3px -3px 15px rgba(155, 0, 255, 0.6), 0 0 10px #9b00ff",
              }}
            />
            <div
              className="absolute bottom-0 left-0 w-6 h-6"
              style={{
                borderBottom: "2px solid #9b00ff",
                borderLeft: "2px solid #9b00ff",
                boxShadow: "-3px 3px 15px rgba(155, 0, 255, 0.6), 0 0 10px #9b00ff",
              }}
            />
            <div
              className="absolute bottom-0 right-0 w-6 h-6"
              style={{
                borderBottom: "2px solid #9b00ff",
                borderRight: "2px solid #9b00ff",
                boxShadow: "3px 3px 15px rgba(155, 0, 255, 0.6), 0 0 10px #9b00ff",
              }}
            />

            {/* Scan Line */}
            <div
              className="absolute left-0 right-0 h-[2px] pointer-events-none"
              style={{
                background: "linear-gradient(90deg, transparent, #9b00ff, #00d4ff, #9b00ff, transparent)",
                boxShadow: "0 0 15px #9b00ff, 0 0 30px rgba(0, 212, 255, 0.5)",
                animation: "scanLine 4s linear infinite",
                opacity: 0.7,
              }}
            />

            {/* Close Button */}
            <button
              onClick={handleClose}
              aria-label="Close contact form"
              className="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group z-20"
              style={{
                background: "linear-gradient(135deg, rgba(61, 0, 102, 0.9), rgba(13, 0, 16, 0.95))",
                border: "1px solid rgba(155, 0, 255, 0.5)",
                boxShadow: "0 0 20px rgba(155, 0, 255, 0.4), 0 0 40px rgba(155, 0, 255, 0.2)",
              }}
              data-testid="holo-close-btn"
            >
              <X
                size={18}
                className="transition-colors duration-300"
                style={{ color: "#9b00ff" }}
              />
              <div
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: "0 0 30px rgba(155, 0, 255, 0.8), inset 0 0 20px rgba(155, 0, 255, 0.3)",
                }}
              />
            </button>

            {/* Header */}
            <div className="text-center mb-4">
              <div
                className="flex items-center justify-center gap-3 mb-3"
                style={{ fontFamily: "'Orbitron', monospace" }}
              >
                <div
                  className="h-[1px] w-12"
                  style={{
                    background: "linear-gradient(90deg, transparent, #00d4ff)",
                  }}
                />
                <span
                  className="text-[10px] tracking-[5px] uppercase"
                  style={{ color: "#00d4ff", opacity: 0.8 }}
                >
                  Secure Channel
                </span>
                <div
                  className="h-[1px] w-12"
                  style={{
                    background: "linear-gradient(270deg, transparent, #00d4ff)",
                  }}
                />
              </div>
              <h2
                className="text-xl md:text-2xl font-bold tracking-wider"
                style={{
                  fontFamily: "'Orbitron', monospace",
                  background: "linear-gradient(135deg, #e0c8ff 0%, #9b00ff 50%, #00d4ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                LET'S CONNECT
              </h2>
              <p
                className="text-xs mt-1"
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  color: "rgba(155, 0, 255, 0.6)",
                  letterSpacing: "1px",
                }}
              >
                Transmission Interface v2.4
              </p>
            </div>

            {/* Divider */}
            <div
              className="h-[1px] mb-4 relative"
              style={{
                background: "linear-gradient(90deg, transparent, #9b00ff, #00d4ff, #9b00ff, transparent)",
                opacity: 0.5,
              }}
            >
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2"
                style={{
                  background: "#9b00ff",
                  boxShadow: "0 0 15px #9b00ff, 0 0 30px #9b00ff",
                  clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                }}
              />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="holo-contact-form">
              {/* Name + Email side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="holo-field">
                  <label
                    className="block text-[11px] tracking-[2px] uppercase mb-1.5 font-semibold"
                    style={{
                      fontFamily: "'Orbitron', monospace",
                      color: "#d8b4fe",
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    required
                    className="w-full px-4 py-2.5 rounded outline-none transition-all duration-300"
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "16px",
                      fontWeight: 500,
                      background: "rgba(13, 0, 16, 0.8)",
                      border: "1px solid rgba(155, 0, 255, 0.4)",
                      color: "#e9d5ff",
                      boxShadow: "inset 0 0 20px rgba(61, 0, 102, 0.3)",
                    }}
                    data-testid="holo-name-input"
                  />
                </div>
                <div className="holo-field">
                  <label
                    className="block text-[11px] tracking-[2px] uppercase mb-1.5 font-semibold"
                    style={{
                      fontFamily: "'Orbitron', monospace",
                      color: "#d8b4fe",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-2.5 rounded outline-none transition-all duration-300"
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "16px",
                      fontWeight: 500,
                      background: "rgba(13, 0, 16, 0.8)",
                      border: "1px solid rgba(155, 0, 255, 0.4)",
                      color: "#e9d5ff",
                      boxShadow: "inset 0 0 20px rgba(61, 0, 102, 0.3)",
                    }}
                    data-testid="holo-email-input"
                  />
                </div>
              </div>

              {/* Phone Field */}
              <div className="holo-field">
                <label
                  className="block text-[11px] tracking-[2px] uppercase mb-1.5 font-semibold"
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    color: "#d8b4fe",
                  }}
                >
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    aria-label="Country code"
                    className="px-2 py-2.5 rounded outline-none transition-all duration-300 cursor-pointer"
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "16px",
                      fontWeight: 500,
                      background: "rgba(13, 0, 16, 0.8)",
                      border: "1px solid rgba(155, 0, 255, 0.4)",
                      color: "#e9d5ff",
                      boxShadow: "inset 0 0 20px rgba(61, 0, 102, 0.3)",
                      width: "128px",
                      colorScheme: "dark",
                    }}
                    data-testid="holo-country-code"
                  >
                    <option value="+971">AE +971</option>
                    <option value="+966">SA +966</option>
                    <option value="+974">QA +974</option>
                    <option value="+965">KW +965</option>
                    <option value="+973">BH +973</option>
                    <option value="+968">OM +968</option>
                    <option value="+20">EG +20</option>
                    <option value="+962">JO +962</option>
                    <option value="+91">IN +91</option>
                    <option value="+92">PK +92</option>
                    <option value="+44">UK +44</option>
                    <option value="+1">US +1</option>
                    <option value="+49">DE +49</option>
                    <option value="+33">FR +33</option>
                    <option value="+61">AU +61</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="50 123 4567"
                    className="flex-1 w-full px-4 py-2.5 rounded outline-none transition-all duration-300"
                    style={{
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "16px",
                      fontWeight: 500,
                      background: "rgba(13, 0, 16, 0.8)",
                      border: "1px solid rgba(155, 0, 255, 0.4)",
                      color: "#e9d5ff",
                      boxShadow: "inset 0 0 20px rgba(61, 0, 102, 0.3)",
                    }}
                    data-testid="holo-phone-input"
                  />
                </div>
              </div>

              {/* Message Field */}
              <div className="holo-field">
                <label
                  className="block text-[11px] tracking-[2px] uppercase mb-1.5 font-semibold"
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    color: "#d8b4fe",
                  }}
                >
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project..."
                  required
                  rows={3}
                  className="w-full px-4 py-2.5 rounded outline-none transition-all duration-300 resize-none"
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: "16px",
                    fontWeight: 500,
                    background: "rgba(13, 0, 16, 0.8)",
                    border: "1px solid rgba(155, 0, 255, 0.4)",
                    color: "#e9d5ff",
                    boxShadow: "inset 0 0 20px rgba(61, 0, 102, 0.3)",
                  }}
                  data-testid="holo-message-input"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={sending}
                className="w-full py-3 rounded relative overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed group"
                style={{
                  fontFamily: "'Orbitron', monospace",
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "4px",
                  textTransform: "uppercase",
                  background: "linear-gradient(135deg, rgba(61, 0, 102, 0.8) 0%, rgba(13, 0, 16, 0.9) 50%, rgba(155, 0, 255, 0.6) 100%)",
                  border: "1px solid rgba(155, 0, 255, 0.5)",
                  color: "#f0e0ff",
                  boxShadow: "0 0 25px rgba(155, 0, 255, 0.4), inset 0 1px 0 rgba(155, 0, 255, 0.3)",
                }}
                data-testid="holo-submit-btn"
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  <Send size={14} />
                  {sending ? "Transmitting..." : "Transmit Message"}
                </span>
                {/* Hover shine effect */}
                <div
                  className="absolute top-0 -left-full w-1/2 h-full skew-x-12 transition-all duration-500 group-hover:left-full"
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
                  }}
                />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes scanLine {
          0% { top: 0; opacity: 0; }
          5% { opacity: 0.7; }
          95% { opacity: 0.7; }
          100% { top: 100%; opacity: 0; }
        }

        @keyframes holoFlicker {
          0%, 100% {
            box-shadow: 0 0 0 1px rgba(155, 0, 255, 0.2), 0 0 60px rgba(155, 0, 255, 0.4), 0 0 120px rgba(155, 0, 255, 0.2), inset 0 0 80px rgba(61, 0, 102, 0.3);
          }
          48% {
            box-shadow: 0 0 0 1px rgba(155, 0, 255, 0.3), 0 0 80px rgba(155, 0, 255, 0.6), 0 0 160px rgba(155, 0, 255, 0.3), inset 0 0 100px rgba(61, 0, 102, 0.4);
          }
          50% {
            box-shadow: 0 0 0 1px rgba(155, 0, 255, 0.1), 0 0 30px rgba(155, 0, 255, 0.2), 0 0 60px rgba(155, 0, 255, 0.1), inset 0 0 40px rgba(61, 0, 102, 0.15);
          }
          52% {
            box-shadow: 0 0 0 1px rgba(155, 0, 255, 0.3), 0 0 80px rgba(155, 0, 255, 0.6), 0 0 160px rgba(155, 0, 255, 0.3), inset 0 0 100px rgba(61, 0, 102, 0.4);
          }
        }

        @keyframes orbFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-30px) scale(1.08); }
        }

        .holo-field input:focus,
        .holo-field textarea:focus {
          border-color: #c084fc !important;
          box-shadow: 0 0 0 2px rgba(192, 132, 252, 0.3), 0 0 30px rgba(155, 0, 255, 0.4), inset 0 0 30px rgba(61, 0, 102, 0.4) !important;
          background: rgba(61, 0, 102, 0.5) !important;
        }

        .holo-field input::placeholder,
        .holo-field textarea::placeholder {
          color: rgba(216, 180, 254, 0.5);
          font-weight: 400;
        }
      `}</style>
    </>
  );
}
